import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Badge, TechStackBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Section, SectionHeader } from '../ui/Section';
import { ClientProject, featuredClients, clientCategories } from '../../data/clients';
import Icon from '@mdi/react';
import { mdiArrowRight } from '@mdi/js';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ClientCardLink = styled(Link)<{ $color: string }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: var(--glass-background);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-card);
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${props => props.$color};
  }
`;

const MockupContainer = styled.div<{ $color: string }>`
  position: relative;
  height: 200px;
  margin: calc(-1 * var(--space-6));
  margin-bottom: var(--space-6);
  background: linear-gradient(135deg, ${props => props.$color}20, ${props => props.$color}05);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
`;

const LaptopMockup = styled.img`
  max-height: 180px;
  max-width: 80%;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15));
`;

const PhoneMockup = styled.img`
  position: absolute;
  right: 10%;
  bottom: 0;
  max-height: 140px;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2));
`;

const PlaceholderMockup = styled.div<{ $color: string }>`
  width: 80%;
  height: 140px;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: ${props => props.$color}30;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-4xl);
  color: ${props => props.$color};
`;

const CardHeader = styled.div`
  margin-bottom: var(--space-4);
`;

const ClientName = styled.h3`
  font-size: var(--text-2xl);
  margin-bottom: var(--space-2);
`;

const ClientTagline = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const CategoryBadge = styled(Badge)<{ $color: string }>`
  background: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const Description = styled.p`
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
`;

const ServicesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
`;

const ResultsList = styled.ul`
  margin-bottom: var(--space-6);
  padding-left: var(--space-4);
`;

const Result = styled.li`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-2);
  position: relative;

  &::before {
    content: '+';
    position: absolute;
    left: -20px;
    color: var(--color-success);
    font-weight: bold;
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
`;

const CardFooter = styled.div`
  display: flex;
  gap: var(--space-3);
  margin-top: auto;
`;

const ViewDetailsText = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-brand-primary);
`;

const ViewAllWrapper = styled.div`
  text-align: center;
  margin-top: var(--space-12);
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-8);
  justify-content: center;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  transition: all var(--transition-fast);
  background: ${props => props.$active ? 'var(--color-brand-primary)' : 'var(--color-border)'};
  color: ${props => props.$active ? 'white' : 'var(--color-text-secondary)'};

  &:hover {
    background: ${props => props.$active ? 'var(--color-brand-primary)' : 'var(--color-border-strong)'};
  }
`;

interface ClientGridProps {
  clients?: ClientProject[];
  showHeader?: boolean;
  showViewAll?: boolean;
  showFilters?: boolean;
  activeFilter?: string;
  onFilterChange?: (filter: string) => void;
}

const categoryColors: Record<string, string> = {
  'ecommerce': '#7AB55C',
  'corporate': '#007aff',
  'webapp': '#511461',
  'mobile': '#ff9500',
};

const categoryLabels: Record<string, string> = {
  'ecommerce': 'E-Commerce',
  'corporate': 'Corporate',
  'webapp': 'Web App',
  'mobile': 'Mobile',
};

export function ClientGrid({
  clients = featuredClients,
  showHeader = true,
  showViewAll = true,
  showFilters = false,
  activeFilter = 'all',
  onFilterChange,
}: ClientGridProps) {
  return (
    <Section>
      {showHeader && (
        <SectionHeader
          title="Client Work"
          subtitle="We've partnered with businesses of all sizes to deliver solutions that drive results."
          centered
        />
      )}

      {showFilters && (
        <FilterBar>
          {clientCategories.map(cat => (
            <FilterButton
              key={cat.id}
              $active={activeFilter === cat.id}
              onClick={() => onFilterChange?.(cat.id)}
            >
              {cat.label}
            </FilterButton>
          ))}
        </FilterBar>
      )}

      <Grid>
        {clients.map(client => (
          <ClientCardLink key={client.id} to={`/portfolio/${client.id}`} $color={client.accentColor}>
            <MockupContainer $color={client.accentColor}>
              {client.imageLaptop ? (
                <>
                  <LaptopMockup src={client.imageLaptop} alt={`${client.name} on laptop`} />
                  {client.imagePhone && (
                    <PhoneMockup src={client.imagePhone} alt={`${client.name} on phone`} />
                  )}
                </>
              ) : (
                <PlaceholderMockup $color={client.accentColor}>
                  {client.name.charAt(0)}
                </PlaceholderMockup>
              )}
            </MockupContainer>

            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                <ClientName>{client.name}</ClientName>
                <CategoryBadge $color={categoryColors[client.category]} size="sm">
                  {categoryLabels[client.category]}
                </CategoryBadge>
              </div>
              <ClientTagline>{client.tagline}</ClientTagline>
            </CardHeader>

            <Description>{client.description}</Description>

            <ServicesList>
              {client.services.slice(0, 4).map(service => (
                <Badge key={service} size="sm" variant="primary">
                  {service}
                </Badge>
              ))}
            </ServicesList>

            {client.results && client.results.length > 0 && (
              <ResultsList>
                {client.results.map(result => (
                  <Result key={result}>{result}</Result>
                ))}
              </ResultsList>
            )}

            <TechStack>
              {client.techStack.map(tech => (
                <TechStackBadge key={tech} tech={tech} />
              ))}
            </TechStack>

            <CardFooter>
              <ViewDetailsText>View Details</ViewDetailsText>
            </CardFooter>
          </ClientCardLink>
        ))}
      </Grid>

      {showViewAll && (
        <ViewAllWrapper>
          <Button as={Link} to="/portfolio" variant="secondary">
            View All Projects
            <Icon path={mdiArrowRight} size={0.8} />
          </Button>
        </ViewAllWrapper>
      )}
    </Section>
  );
}
